import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useState, type KeyboardEvent } from 'react'
import { EmptyState, Kbd } from '../../ui/primitives.tsx'
import type { Column, Task, User } from '../../lib/types.ts'
import { SortableTaskCard } from './TaskCard.tsx'
import styles from './board.module.css'

const DOT_CLASS: Record<Column['color'], string> = {
  gray: styles.dotGray!,
  blue: styles.dotBlue!,
  steel: styles.dotSteel!,
  green: styles.dotGreen!,
}

export function BoardColumn({
  column,
  tasks,
  users,
  quickAddOpen,
  onQuickAddOpenChange,
  onQuickAdd,
  onOpenTask,
}: {
  column: Column
  tasks: Task[]
  users: User[]
  /** Controlled by the board: only one quick-add is open at a time, and `n` opens the first. */
  quickAddOpen: boolean
  onQuickAddOpenChange: (open: boolean) => void
  onQuickAdd: (title: string, columnId: number) => void
  onOpenTask: (id: number) => void
}): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${column.id}` })

  return (
    <section className={styles.column} aria-label={column.name}>
      <header className={styles.columnHead}>
        <span className={`${styles.columnDot} ${DOT_CLASS[column.color]}`} />
        <span className={styles.columnName}>{column.name}</span>
        <span className={styles.columnCount}>{tasks.length}</span>
        {/* `.columnAdd` był ostylowany od etapu 2 i nigdy nierenderowany (ui-spec §5.2).
            Otwiera to samo quick-add, co przycisk na dole — kolumna z długą listą nie zmusza
            do przewinięcia na sam dół, żeby dopisać zadanie. */}
        <button
          type="button"
          className={styles.columnAdd}
          aria-label={`Dodaj zadanie w kolumnie ${column.name}`}
          onClick={() => onQuickAddOpenChange(true)}
        >
          +
        </button>
      </header>

      <div ref={setNodeRef} className={styles.columnBody} data-over={isOver || undefined}>
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <SortableTaskCard
              key={task.id}
              task={task}
              assignee={users.find((u) => u.id === task.assigneeId) ?? null}
              dimmed={column.color === 'green'}
              onOpen={onOpenTask}
            />
          ))}
        </SortableContext>

        {tasks.length === 0 && (
          <div className={styles.emptyWrap}>
            {/* ui-spec §5.2 chce tu jednej akcji, nie samego zdania — pusty stan bez wyjścia
                zostawia człowieka z informacją zamiast z możliwością. */}
            <EmptyState
              text="Ta kolumna jest pusta"
              action={{ label: 'Dodaj zadanie', onClick: () => onQuickAddOpenChange(true) }}
            />
          </div>
        )}
      </div>

      <QuickAdd
        columnName={column.name}
        open={quickAddOpen}
        onOpenChange={onQuickAddOpenChange}
        onSubmit={(title) => onQuickAdd(title, column.id)}
      />
    </section>
  )
}

/**
 * Enter creates the task and keeps focus, so a series of ideas can be typed without touching the
 * mouse (ui-spec §5.2).
 */
function QuickAdd({
  columnName,
  open,
  onOpenChange,
  onSubmit,
}: {
  columnName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (title: string) => void
}): React.JSX.Element {
  const [value, setValue] = useState('')

  const commit = (): void => {
    const title = value.trim()
    if (!title) return
    onSubmit(title)
    setValue('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit()
    }
    if (event.key === 'Escape') {
      event.stopPropagation()
      setValue('')
      onOpenChange(false)
    }
  }

  if (!open) {
    return (
      <div className={styles.quickAdd}>
        <button
          type="button"
          className={styles.quickAddRest}
          onClick={() => onOpenChange(true)}
          aria-label={`Dodaj zadanie do kolumny ${columnName}`}
        >
          + Dodaj zadanie
        </button>
      </div>
    )
  }

  return (
    <div className={styles.quickAdd}>
      <div className={styles.quickAddActive}>
        <input
          ref={focusOnMount}
          className={styles.quickAddInput}
          value={value}
          placeholder="Tytuł zadania"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (!value.trim()) onOpenChange(false)
          }}
        />
        <Kbd>↵</Kbd>
      </div>
    </div>
  )
}

/**
 * Stable ref callback. Inline `ref={(el) => el?.focus()}` gets a new identity on every render, so
 * React detaches and re-attaches the ref — and calls focus() again — on every keystroke in the
 * quick-add field. Declared once at module scope, it runs on mount and no more.
 */
function focusOnMount(element: HTMLInputElement | null): void {
  element?.focus()
}
